import { Drawer, Grid } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

export const RESPONSIVE_SIDE_DRAWER_WIDTH = 'min(560px, 42vw)';
export const RESPONSIVE_SIDE_DRAWER_PAGE_CLASS = 'page--responsive-side-drawer-open';
export const RESPONSIVE_SIDE_DRAWER_WIDTH_WIDE = 'min(720px, 50vw)';

const resizedPageCounts = new WeakMap();
const initiallyResizedPages = new WeakMap();
const initialPageDrawerWidths = new WeakMap();
const resizedPageWidths = new WeakMap();

const addResizeClass = (pageElement, drawerWidth) => {
  const count = resizedPageCounts.get(pageElement) || 0;
  if (!count) {
    initiallyResizedPages.set(pageElement, pageElement.classList.contains(RESPONSIVE_SIDE_DRAWER_PAGE_CLASS));
    initialPageDrawerWidths.set(pageElement, pageElement.style.getPropertyValue('--responsive-side-drawer-width'));
  }

  const widths = resizedPageWidths.get(pageElement) || [];
  widths.push(drawerWidth);

  resizedPageWidths.set(pageElement, widths);
  resizedPageCounts.set(pageElement, count + 1);
  pageElement.classList.add(RESPONSIVE_SIDE_DRAWER_PAGE_CLASS);
  pageElement.style.setProperty('--responsive-side-drawer-width', drawerWidth);
}

const removeResizeClass = (pageElement, drawerWidth) => {
  const count = resizedPageCounts.get(pageElement) || 0;
  const widths = resizedPageWidths.get(pageElement) || [];
  const widthIndex = widths.lastIndexOf(drawerWidth);

  if (widthIndex >= 0) {
    widths.splice(widthIndex, 1);
  } else {
    widths.pop();
  }

  if (count <= 1) {
    const hadResizeClass = initiallyResizedPages.get(pageElement);
    const initialDrawerWidth = initialPageDrawerWidths.get(pageElement);

    resizedPageCounts.delete(pageElement);
    initiallyResizedPages.delete(pageElement);
    initialPageDrawerWidths.delete(pageElement);
    resizedPageWidths.delete(pageElement);

    if (initialDrawerWidth) {
      pageElement.style.setProperty('--responsive-side-drawer-width', initialDrawerWidth);
    } else {
      pageElement.style.removeProperty('--responsive-side-drawer-width');
    }

    if (!hadResizeClass) {
      pageElement.classList.remove(RESPONSIVE_SIDE_DRAWER_PAGE_CLASS);
    }
    return;
  }

  resizedPageWidths.set(pageElement, widths);
  pageElement.style.setProperty('--responsive-side-drawer-width', widths[widths.length - 1]);
  resizedPageCounts.set(pageElement, count - 1);
}

function ResponsiveSideDrawer({
  desktopWidth = RESPONSIVE_SIDE_DRAWER_WIDTH,
  mobileWidth = 'min(720px, 100vw)',
  mask = false,
  pageSelector = '.page',
  resizePage = true,
  open,
  ...props
}) {
  const screens = Grid.useBreakpoint()

  useEffect(() => {
    if (!open || !resizePage || typeof document === 'undefined') return undefined;

    const drawerWidth = typeof desktopWidth === 'number' ? `${desktopWidth}px` : desktopWidth;
    const pages = document.querySelectorAll(pageSelector);
    const activePage = pages[pages.length - 1];

    if (!activePage) return undefined;

    addResizeClass(activePage, drawerWidth);

    return () => removeResizeClass(activePage, drawerWidth);
  }, [desktopWidth, open, pageSelector, resizePage]);

  return (
    <Drawer
      mask={mask}
      open={open}
      width={screens.xl ? desktopWidth : mobileWidth}
      {...props}
    />
  )
}

ResponsiveSideDrawer.propTypes = {
  desktopWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mask: PropTypes.bool,
  mobileWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  open: PropTypes.bool,
  pageSelector: PropTypes.string,
  resizePage: PropTypes.bool,
}

ResponsiveSideDrawer.DRAWER_WIDTH = RESPONSIVE_SIDE_DRAWER_WIDTH;
ResponsiveSideDrawer.DRAWER_WIDTH_WIDE = RESPONSIVE_SIDE_DRAWER_WIDTH_WIDE;
ResponsiveSideDrawer.PAGE_CLASS = RESPONSIVE_SIDE_DRAWER_PAGE_CLASS;

export default ResponsiveSideDrawer;
